import { LIMIT_DEFAULT } from '../../constants'
import { getAfterCursor } from '../../utils/graphqlUtil'
import { Notification } from '../notifications/notificationsSlice'
import { gql } from 'graphql-request'

export interface ReportListingParams {
  isScheduled?: boolean
  page: number
  pageLimit?: number
  query?: string
}

export interface ReportCatalogParams {
  page: number
  pageLimit?: number
  query?: string
}

export const getReportListingFilters = (params: ReportListingParams) => {
  const { isScheduled, page, pageLimit = LIMIT_DEFAULT, query } = params
  const cursor = getAfterCursor(page, pageLimit)
  let paramStr = `first: ${pageLimit}, after:"${cursor}"`

  if (isScheduled) {
    paramStr += `, booleanFilter: [{key: IS_SCHEDULED, value: true}]`
  }

  if (query && query.trim()) {
    paramStr += `, query: "${query.trim()}"`
  }

  return `(${paramStr}, sortField: CREATED_AT)`
}

export const getReportCatalogFilters = (params: ReportCatalogParams) => {
  const { page, pageLimit = LIMIT_DEFAULT, query } = params
  const cursor = getAfterCursor(page, pageLimit)
  let paramStr = `first: ${pageLimit}, after:"${cursor}"`

  if (query && query.trim()) {
    paramStr += `, query: "${query.trim()}"`
  }

  return `(${paramStr})`
}

export const queryReportListing = (params: ReportListingParams) => {
  const filterString = getReportListingFilters(params)
  return gql`
    {
      reportHistory${filterString} {
        count
        edges {
          node {
            id
            title
            reportType
            reportFormat
            notificationType
            notificationConfiguration {
              emailIDs
              notificationId
            }
            createdAt
            updateTimestamp
            lastExecutionTimestamp
            cronExpression
            notification {
              id
              title
              icon
              createdAt
              updateTimestamp
              workflowStatus
              attachments {
                id
                name
                url
                status
              }
              module {
                moduleObject {
                  ... on DatasourceNotification {
                    id
                    type
                    dsType
                  }
                }
              }
            }
          }
        }
      }
    }
  `
}

export const queryReportCatalog = (params: ReportCatalogParams) => {
  const filterString = getReportCatalogFilters(params)
  return gql`
    {
      reportCatalog${filterString} {
        count
        edges {
          node {
            id
            reportType
            displayName
            description
            supportedFormats
            createdAt
            createdBy
            updateTimestamp
          }
        }
      }
    }
  `
}

export interface ReportListingItem {
  id: string
  reportType: string
  notificationType: string
  reportName: string
  reportFormat?: string
  notificationConfiguration: {
    emailIDs: string[]
    notificationId: string
  }
  createdAt: string
  updateTimestamp: string
  lastExecutionTimestamp: string
  cronExpression: string
  notification: ReportsNotification
}

export interface ReportCatalogItem {
  id: string
  reportType: string
  displayName: string
  description: string
  supportedFormats: string[]
  createdAt: string
  createdBy: string
  updateTimestamp: string
}

export interface DatasourceNotification {
  id: string
  type: string
  dsType: string
}

export interface ReportsNotification extends Notification {
  datasourceNotification?: DatasourceNotification
}

export const mapQueryReportListing = (raw: any): { list: ReportListingItem[]; count: number } => {
  try {
    const list =
      raw?.reportHistory?.edges?.map(({ node }: { node: any }) => ({
        id: node?.id || '',
        reportType: node?.reportType || '',
        reportName: node?.title || '',
        reportFormat: node?.reportFormat || '',
        notificationType: node?.notificationType || '',
        notificationConfiguration: {
          emailIDs: node?.notificationConfiguration?.emailIDs || [],
          notificationId: node?.notificationConfiguration?.notificationId || ''
        },
        createdAt: node?.createdAt || '',
        updateTimestamp: node?.updateTimestamp || '',
        lastExecutionTimestamp: node?.lastExecutionTimestamp || '',
        cronExpression: node?.cronExpression || '',
        notification: node?.notification
          ? {
              id: node.notification.id,
              title: node.notification.title,
              icon: node.notification.icon,
              createdAt: node.notification.createdAt,
              updateTimestamp: node.notification.updateTimestamp,
              workflowStatus: node.notification.workflowStatus,
              attachments:
                node.notification.attachments?.map((att: any) => ({
                  id: att.id,
                  name: att.name,
                  url: att.url,
                  status: att.status
                })) || [],
              datasourceNotification: node.notification.module?.moduleObject
                ? {
                    id: node.notification.module.moduleObject.id || '',
                    type: node.notification.module.moduleObject.type || '',
                    dsType: node.notification.module.moduleObject.dsType || ''
                  }
                : undefined
            }
          : undefined
      })) || []

    return {
      list,
      count: raw?.reportHistory?.count || 0
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const mapQueryReportCatalog = (raw: any): { list: ReportCatalogItem[]; count: number } => {
  try {
    const list =
      raw?.reportCatalog?.edges?.map(({ node }: { node: any }) => ({
        id: node?.id || '',
        reportType: node?.reportType || '',
        displayName: node?.displayName || '',
        description: node?.description || '',
        supportedFormats: node?.supportedFormats || [],
        createdAt: node?.createdAt || '',
        createdBy: node?.createdBy || '',
        updateTimestamp: node?.updateTimestamp || ''
      })) || []

    return {
      list,
      count: raw?.reportCatalog?.count || 0
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}
